import React, { useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  Grid,
  Layout,
  Menu,
  Space,
  Typography,
} from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import {
  BrowserRouter,
  Link as RouterLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import './App.css';
import LoginForm from './components/LoginForm';
import Console from './components/Console';
import Authorize from './components/Authorize';
import MyApps from './components/MyApps';
import AdminMyApps from './components/AdminMyApps';
import Security from './components/Security';
import AdminReset2FA from './components/AdminReset2FA';
import DashboardSkeleton from './components/DashboardSkeleton';
import About from './components/About';
import UsersAPI from './api/user';
import AuthAPI from './api/auth';
import Cookies from 'universal-cookie';
import TicketsAPI from './api/ticket';
import { ThemeProvider } from './theme/ThemeProvider';
import ThemePicker from './theme/ThemePicker';
import ThemeSync from './theme/ThemeSync';


const { Title } = Typography;

const SYS_ADMIN_BIT = 8;

function isAdmin(user) {
  return !!(user && (Number(user.sys_roles) & SYS_ADMIN_BIT));
}

function App() {

  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (!user) {
      return;
    }
    TicketsAPI.search({},
      (res) => {
        setSessions(res);
      },
      (err) => {
        console.log(err);
      }
    )
    return () => {

    }
  }, [user]);


  const cookies = new Cookies();

  let service = null;
  try {
    const st_requestService = window.atob(cookies.get('SSO_requestService'));
    console.log('requestService', st_requestService);
    service = JSON.parse(st_requestService);
  } catch (e) {
    console.log(e);
  }

  const logout = () => {
    AuthAPI.logout(
      (res) => {
        setUser(null);
      },
      (err) => {
        console.log(err);
      });
  }

  const onLogin = () => {
    setIsLoading(true);
    UsersAPI.me(
      (user) => {
        setUser(user);
        setIsLoading(false);
      },
      (err) => {
        setUser(null);
        setIsLoading(false);
      })
  }

  useEffect(() => {
    onLogin();
    return () => {

    }
  }, []);


  const homeContent = <>
    {!user && <LoginForm onLogin={onLogin} />}
    {user && <>
      {service && <Authorize user={user} service={service} />}
      <Console sessions={sessions} user={user} onLogout={logout} />
    </>}
  </>;

  const gate = (node) => {
    if (!user) return <LoginForm onLogin={onLogin} />;
    return node;
  };

  return (
    <ThemeProvider>
      <ThemeSync user={user} />
      <BrowserRouter basename="/html">
        <Layout style={{ minHeight: '100vh' }}>
          <AppHeader user={user} service={service} />
          <Layout.Content style={{ padding: '24px 16px' }}>
            <div style={{ maxWidth: 960, margin: '0 auto' }}>
              {isLoading ? (
                <DashboardSkeleton />
              ) : (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <Routes>
                    <Route path="/" element={homeContent} />
                    <Route path="/about" element={<About />} />
                    <Route path="/myapps" element={gate(<MyApps user={user} />)} />
                    <Route path="/security" element={gate(<Security />)} />
                    <Route
                      path="/admin/myapps"
                      element={
                        user
                          ? (isAdmin(user) ? <AdminMyApps user={user} /> : <Navigate to="/" replace />)
                          : <LoginForm onLogin={onLogin} />
                      }
                    />
                    <Route
                      path="/admin/reset-2fa"
                      element={
                        user
                          ? (isAdmin(user) ? <AdminReset2FA /> : <Navigate to="/" replace />)
                          : <LoginForm onLogin={onLogin} />
                      }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Space>
              )}
            </div>
          </Layout.Content>
          <Layout.Footer
            style={{
              textAlign: 'center',
              borderTop: '1px solid var(--ant-color-border-secondary, rgba(0, 0, 0, 0.06))',
              padding: '20px 16px',
            }}
          >
            <Space split={<Typography.Text type="secondary">·</Typography.Text>} wrap>
              <a href="/wiki/">Wiki</a>
              <RouterLink to="/about">About</RouterLink>
              <a href="https://www.it.chula.ac.th/" target="_blank" rel="noreferrer">
                Office of IT
              </a>
            </Space>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginTop: 8, fontSize: 12 }}
            >
              ChulaSSO 2.0 · A solution by Krerk Piromsopa, Ph.D. for Chulalongkorn University
            </Typography.Text>
          </Layout.Footer>
        </Layout>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function AppHeader({ user, service }) {
  const location = useLocation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const showNav = !!user && !service;
  const isCompact = !screens.md;

  const items = [
    { key: '/', label: 'Home' },
    { key: '/myapps', label: 'My Apps' },
    { key: '/security', label: 'Security' },
  ];
  if (isAdmin(user)) {
    items.push({ key: '/admin/myapps', label: 'Admin — App Requests' });
    items.push({ key: '/admin/reset-2fa', label: 'Admin — Reset 2FA' });
  }

  const onNavClick = ({ key }) => {
    navigate(key);
    setDrawerOpen(false);
  };

  return (
    <Layout.Header style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      padding: isCompact ? '0 16px' : undefined,
      boxShadow: '0 1px 0 rgba(0, 0, 0, 0.06)',
      position: 'sticky',
      top: 0,
      zIndex: 10,
    }}>
      <Space size={12} align="center" style={{ minWidth: 0 }}>
        <img src="/html/logo-chula-color.svg" height={36} alt="Chula" style={{ display: 'block' }} />
        {screens.xs ? null : (
          <Title level={4} style={{ margin: 0, whiteSpace: 'nowrap' }}>SSO 2.0</Title>
        )}
      </Space>
      <Space size={4} align="center" style={{ flex: 1, justifyContent: 'flex-end' }}>
        {showNav && !isCompact && (
          <Menu
            mode="horizontal"
            selectedKeys={[location.pathname]}
            onClick={onNavClick}
            items={items}
            style={{ background: 'transparent', borderBottom: 'none', minWidth: 0, flex: 'none' }}
          />
        )}
        <ThemePicker />
        {showNav && isCompact && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          />
        )}
      </Space>
      <Drawer
        title="Menu"
        placement="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={Math.min(280, typeof window !== 'undefined' ? window.innerWidth - 48 : 280)}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="vertical"
          selectedKeys={[location.pathname]}
          onClick={onNavClick}
          items={items}
          style={{ borderInlineEnd: 'none' }}
        />
      </Drawer>
    </Layout.Header>
  );
}

export default App;
